// components/ResetPassword/ResetPassword.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  OutlinedInput,
  Slide,
  Fade,
  Zoom,
  Avatar,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  useMediaQuery,
  LinearProgress,
} from '@mui/material';
import {
  Search,
  Refresh,
  Person,
  Phone,
  Email,
  Business,
  Lock,
  LockOpen,
  Visibility,
  VisibilityOff,
  VpnKey,
  Cancel,
  History,
  AdminPanelSettings,
  People,
  Security,
  CheckCircle,
  WarningAmber,
} from '@mui/icons-material';
import { styled, keyframes } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../../../components/sidebars/Sidebar_hotelAdmin';
import TopNav from '../../../components/TopNav_hotelAdmin';

// Animations
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled Components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: '16px',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: theme.shadows[8],
  },
}));

const SearchCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #0c0421 0%, #0a041f 100%)',
  color: 'white',
  borderRadius: '16px',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const StaffCard = styled(Card)(({ theme, selected }) => ({
  cursor: 'pointer',
  border: selected ? `2px solid ${theme.palette.primary.main}` : '1px solid #e0e0e0',
  borderRadius: '12px',
  transition: 'all 0.3s ease',
  animation: `${fadeInUp} 0.5s ease-out`,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const ResetCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1a024b 0%, #140522 100%)',
  color: 'white',
  borderRadius: '16px',
  padding: theme.spacing(3),
}));

const StatCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.action.hover} 100%)`,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '20px',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[12],
  },
}));

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// Helper Functions - FIXED to properly get hotel code
const getCurrentAdminId = () => {
  try {
    const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
    console.log('Getting admin ID from profile:', storedProfile);
    return storedProfile.staff_id || storedProfile.id || storedProfile.staffId || null;
  } catch (error) {
    console.error('Error getting admin ID:', error);
    return null;
  }
};

const getCurrentAdminName = () => {
  try {
    const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
    return storedProfile.first_name || storedProfile.name || 'Admin';
  } catch (error) {
    console.error('Error getting admin name:', error);
    return 'Admin';
  }
};

const getHotelCode = () => {
  try {
    const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
    // Try multiple possible keys
    const hotelCode = storedProfile.hotelcode || 
                     storedProfile.hotel_code || 
                     storedProfile.hotelCode || 
                     storedProfile.hotelcode || 
                     '';
    console.log('Retrieved hotel code:', hotelCode);
    return hotelCode;
  } catch (error) {
    console.error('Error getting hotel code:', error);
    return '';
  }
};

const ResetPassword = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:768px)');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [searching, setSearching] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [recentResets, setRecentResets] = useState([]);
  const [loadingResets, setLoadingResets] = useState(false);
  const [stats, setStats] = useState({ total_staff: 0, total_resets: 0 });

  // Get hotel code - log it for debugging
  const hotelCode = getHotelCode();
  const adminId = getCurrentAdminId();
  const adminName = getCurrentAdminName();

  console.log('Hotel Code:', hotelCode);
  console.log('Admin ID:', adminId);
  console.log('Admin Name:', adminName);

  // Password validation
  const [passwordError, setPasswordError] = useState('');
  const [confirmError, setConfirmError] = useState('');

  // Load data on mount
  useEffect(() => {
    loadRecentResets();
    loadStats();
  }, []);

  // Debounce search
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm.length >= 2) {
        handleSearchStaff();
      } else {
        setSearchResults([]);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const loadStats = async () => {
    try {
      // Get all staff for this hotel
      const staffResponse = await axios.get(`${API_BASE_URL}/hotel/admin/reset_staff_password.php`, {
        params: {
          action: 'search_staff',
          search: '',
          hotel_code: hotelCode
        }
      });
      
      console.log('Stats response:', staffResponse.data);
      
      if (staffResponse.data.success) {
        setStats(prev => ({ ...prev, total_staff: staffResponse.data.data?.length || 0 }));
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadRecentResets = async () => {
    setLoadingResets(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/hotel/admin/password_reset_logs.php`, {
        params: {
          hotel_code: hotelCode,
          limit: 20
        }
      });
      console.log('Recent resets response:', response.data);
      
      if (response.data.success) {
        setRecentResets(response.data.data || []);
        setStats(prev => ({ ...prev, total_resets: response.data.data?.length || 0 }));
      }
    } catch (error) {
      console.error('Error loading reset logs:', error);
      showSnackbar('Failed to load reset logs', 'error');
    } finally {
      setLoadingResets(false);
    }
  };

  const handleSearchStaff = async () => {
    if (!searchTerm.trim() || searchTerm.length < 2) {
      showSnackbar('Please enter at least 2 characters to search', 'warning');
      return;
    }

    setSearching(true);
    try {
      const url = `${API_BASE_URL}/hotel/admin/reset_staff_password.php`;
      const params = {
        action: 'search_staff',
        search: searchTerm.trim(),
        hotel_code: hotelCode
      };
      
      console.log('Search URL:', url);
      console.log('Search params:', params);
      
      const response = await axios.get(url, { params });

      console.log('Search response:', response.data);

      if (response.data.success) {
        setSearchResults(response.data.data || []);
        if (response.data.data.length === 0) {
          showSnackbar('No staff members found', 'info');
        }
      } else {
        showSnackbar(response.data.message || 'Search failed', 'error');
      }
    } catch (error) {
      console.error('Error searching staff:', error);
      console.error('Error details:', error.response?.data);
      showSnackbar('Failed to search staff: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setSearching(false);
    }
  };

  const handleSelectStaff = (staff) => {
    setSelectedStaff(staff);
    setSearchResults([]);
    setSearchTerm('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setConfirmError('');
    showSnackbar(`Selected: ${staff.first_name} ${staff.other_names}`, 'success');
  };

  const handleClearSelection = () => {
    setSelectedStaff(null);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setConfirmError('');
  };

  const validatePassword = (password) => {
    if (password.length < 6) {
      return 'Password must be at least 6 characters long';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one number';
    }
    return '';
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setNewPassword(value);
    const error = validatePassword(value);
    setPasswordError(error);
    
    if (confirmPassword && value !== confirmPassword) {
      setConfirmError('Passwords do not match');
    } else if (confirmPassword && value === confirmPassword) {
      setConfirmError('');
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);
    if (newPassword && value !== newPassword) {
      setConfirmError('Passwords do not match');
    } else {
      setConfirmError('');
    }
  };

  const handleResetPassword = () => {
    if (!selectedStaff) {
      showSnackbar('Please select a staff member', 'error');
      return;
    }

    if (!newPassword) {
      showSnackbar('Please enter a new password', 'error');
      return;
    }

    if (passwordError) {
      showSnackbar(passwordError, 'error');
      return;
    }

    if (newPassword !== confirmPassword) {
      showSnackbar('Passwords do not match', 'error');
      return;
    }

    setOpenConfirmDialog(true);
  };

  const confirmResetPassword = async () => {
    setResetting(true);
    setOpenConfirmDialog(false);

    try {
      const payload = {
        action: 'reset_password',
        staff_id: selectedStaff.id,
        new_password: newPassword,
        hotel_code: hotelCode,
        admin_id: adminId,
        admin_name: adminName
      };
      
      console.log('Reset payload:', payload);

      const response = await axios.post(`${API_BASE_URL}/hotel/admin/reset_staff_password.php`, payload);

      console.log('Reset response:', response.data);

      if (response.data.success) {
        showSnackbar(response.data.message, 'success');
        handleClearSelection();
        loadRecentResets();
        loadStats();
      } else {
        showSnackbar(response.data.message || 'Failed to reset password', 'error');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      console.error('Error details:', error.response?.data);
      showSnackbar('Failed to reset password: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setResetting(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearchStaff();
    }
  };

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  return (
    <Box sx={{ display: 'flex', bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          mt: 0,
          maxWidth: '1400px',
          mx: 'auto',
          transition: 'margin 0.3s ease',
        }}
      >
        <TopNav
          onLogout={() => navigate('/HotelAdmin')}
          onNotificationClick={() => alert('Notifications')}
          toggleSidebar={toggleSidebar}
        />

        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 3,
            mt: 3,
            background: 'linear-gradient(135deg, #0e1da3 0%, #320e65 100%)',
            color: 'white',
            borderRadius: 3,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              <VpnKey sx={{ mr: 1, verticalAlign: 'middle' }} />
              Staff Password Reset
            </Typography>
            <Typography variant="subtitle1">
              <AdminPanelSettings sx={{ mr: 1, verticalAlign: 'middle', fontSize: '18px' }} />
              Admin: {adminName} - Securely reset staff passwords
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', mt: 1 }}>
              Hotel Code: {hotelCode}
            </Typography>
          </Box>
          <Box
            sx={{
              position: 'absolute',
              right: -50,
              bottom: -50,
              opacity: 0.1,
              transform: 'rotate(-15deg)',
            }}
          >
            <Security sx={{ fontSize: 200 }} />
          </Box>
        </Paper>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight="bold" color="#1976D2">
                      {stats.total_staff}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">Total Staff</Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: '#1976D2', width: 45, height: 45 }}>
                    <People />
                  </Avatar>
                </Box>
              </CardContent>
            </StatCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight="bold" color="#4CAF50">
                      {stats.total_resets}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">Total Resets</Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: '#4CAF50', width: 45, height: 45 }}>
                    <VpnKey />
                  </Avatar>
                </Box>
              </CardContent>
            </StatCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight="bold" color="#FF9800">
                      {selectedStaff ? 1 : 0}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">Selected Staff</Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: '#FF9800', width: 45, height: 45 }}>
                    <Person />
                  </Avatar>
                </Box>
              </CardContent>
            </StatCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight="bold" color="#9C27B0">
                      {newPassword ? '✓' : '—'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">Password Set</Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: '#9C27B0', width: 45, height: 45 }}>
                    <Lock />
                  </Avatar>
                </Box>
              </CardContent>
            </StatCard>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          {/* Left Column - Search and Selection */}
          <Grid item xs={12} md={7}>
            {/* Search Card */}
            <SearchCard>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Search />
                Search Staff
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>
                Enter staff name, phone, email, or staff ID to search
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  placeholder="Search by name, phone, email, or staff ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={!!selectedStaff}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      color: 'white',
                      '& fieldset': {
                        borderColor: 'rgba(255,255,255,0.3)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255,255,255,0.5)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'white',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255,255,255,0.7)',
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: 'white',
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {searching ? (
                          <CircularProgress size={24} sx={{ color: 'white' }} />
                        ) : (
                          <IconButton
                            onClick={handleSearchStaff}
                            sx={{ color: 'white' }}
                            disabled={searchTerm.length < 2}
                          >
                            <Search />
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, opacity: 0.8 }}>
                    Found {searchResults.length} staff member(s)
                  </Typography>
                  <List sx={{ maxHeight: 400, overflow: 'auto', bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1 }}>
                    {searchResults.map((staff, index) => (
                      <Zoom in timeout={index * 50} key={staff.id}>
                        <ListItem
                          button
                          onClick={() => handleSelectStaff(staff)}
                          sx={{
                            borderBottom: '1px solid rgba(255,255,255,0.1)',
                            '&:hover': {
                              bgcolor: 'rgba(255,255,255,0.1)',
                            },
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: '#1976D2' }}>
                              <Person />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography sx={{ color: 'white', fontWeight: 'bold' }}>
                                {staff.first_name} {staff.other_names}
                              </Typography>
                            }
                            secondary={
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                                <Chip
                                  size="small"
                                  label={staff.staff_id}
                                  icon={<Person sx={{ fontSize: 14 }} />}
                                  sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}
                                  variant="outlined"
                                />
                                <Chip
                                  size="small"
                                  label={staff.phone}
                                  icon={<Phone sx={{ fontSize: 14 }} />}
                                  sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}
                                  variant="outlined"
                                />
                                <Chip
                                  size="small"
                                  label={staff.department}
                                  icon={<Business sx={{ fontSize: 14 }} />}
                                  sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}
                                  variant="outlined"
                                />
                              </Box>
                            }
                          />
                        </ListItem>
                      </Zoom>
                    ))}
                  </List>
                </Box>
              )}
            </SearchCard>

            {/* Selected Staff Card */}
            {selectedStaff && (
              <Fade in>
                <StaffCard selected>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
                          {selectedStaff.first_name} {selectedStaff.other_names}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                          <Chip
                            label={`Staff ID: ${selectedStaff.staff_id}`}
                            icon={<Person />}
                            color="primary"
                            variant="outlined"
                          />
                          <Chip
                            label={`Phone: ${selectedStaff.phone}`}
                            icon={<Phone />}
                            color="primary"
                            variant="outlined"
                          />
                          <Chip
                            label={`Email: ${selectedStaff.email}`}
                            icon={<Email />}
                            color="primary"
                            variant="outlined"
                          />
                          <Chip
                            label={`Department: ${selectedStaff.department}`}
                            icon={<Business />}
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                      <Tooltip title="Clear selection">
                        <IconButton color="error" onClick={handleClearSelection}>
                          <Cancel />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </StaffCard>
              </Fade>
            )}
          </Grid>

          {/* Right Column - Reset Password */}
          <Grid item xs={12} md={5}>
            <ResetCard>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LockOpen />
                Reset Password
              </Typography>

              <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.2)' }} />

              {!selectedStaff ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Lock sx={{ fontSize: 60, opacity: 0.3, mb: 2 }} />
                  <Typography variant="body1" sx={{ opacity: 0.7 }}>
                    Search and select a staff member to reset their password
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Typography variant="body2" sx={{ mb: 2, opacity: 0.7 }}>
                    Reset password for <strong>{selectedStaff.first_name} {selectedStaff.other_names}</strong>
                  </Typography>

                  <FormControl fullWidth sx={{ mb: 2 }} variant="outlined">
                    <InputLabel htmlFor="new-password" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      New Password
                    </InputLabel>
                    <OutlinedInput
                      id="new-password"
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={handlePasswordChange}
                      label="New Password"
                      sx={{
                        color: 'white',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255,255,255,0.3)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255,255,255,0.5)',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'white',
                        },
                      }}
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            onClick={togglePasswordVisibility}
                            sx={{ color: 'rgba(255,255,255,0.7)' }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      }
                    />
                    {passwordError && (
                      <Typography variant="caption" sx={{ color: '#ff6b6b', mt: 1 }}>
                        {passwordError}
                      </Typography>
                    )}
                    <Typography variant="caption" sx={{ opacity: 0.6, mt: 0.5 }}>
                      Must be at least 6 characters with uppercase, lowercase, and number
                    </Typography>
                  </FormControl>

                  <FormControl fullWidth sx={{ mb: 3 }} variant="outlined">
                    <InputLabel htmlFor="confirm-password" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      Confirm Password
                    </InputLabel>
                    <OutlinedInput
                      id="confirm-password"
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={handleConfirmPasswordChange}
                      label="Confirm Password"
                      sx={{
                        color: 'white',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: confirmError ? '#ff6b6b' : 'rgba(255,255,255,0.3)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: confirmError ? '#ff6b6b' : 'rgba(255,255,255,0.5)',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: confirmError ? '#ff6b6b' : 'white',
                        },
                      }}
                    />
                    {confirmError && (
                      <Typography variant="caption" sx={{ color: '#ff6b6b', mt: 1 }}>
                        {confirmError}
                      </Typography>
                    )}
                  </FormControl>

                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleResetPassword}
                    disabled={
                      resetting ||
                      !newPassword ||
                      !!passwordError ||
                      !!confirmError ||
                      newPassword !== confirmPassword
                    }
                    sx={{
                      py: 1.5,
                      background: 'linear-gradient(135deg, #1976D2 0%, #0D47A1 100%)',
                      '&:hover': {
                        transform: 'scale(1.02)',
                        transition: 'transform 0.2s ease',
                      },
                      '&:disabled': {
                        background: 'linear-gradient(135deg, #9e9e9e 0%, #757575 100%)',
                      },
                    }}
                  >
                    {resetting ? (
                      <CircularProgress size={24} sx={{ color: 'white' }} />
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <VpnKey />
                        Reset Password
                      </Box>
                    )}
                  </Button>
                </Box>
              )}
            </ResetCard>
          </Grid>

          {/* Recent Password Resets */}
          <Grid item xs={12}>
            <StyledPaper>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <History />
                  Recent Password Resets
                  <Chip
                    size="small"
                    label={recentResets.length}
                    color="primary"
                    sx={{ ml: 1 }}
                  />
                </Typography>
                <Button
                  size="small"
                  onClick={loadRecentResets}
                  startIcon={<Refresh />}
                  variant="outlined"
                >
                  Refresh
                </Button>
              </Box>

              {loadingResets ? (
                <Box sx={{ py: 3 }}>
                  <LinearProgress />
                  <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', mt: 1 }}>
                    Loading reset logs...
                  </Typography>
                </Box>
              ) : recentResets.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <History sx={{ fontSize: 60, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                  <Typography variant="body2" color="textSecondary">
                    No password resets recorded yet
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell><strong>Staff</strong></TableCell>
                        <TableCell><strong>Staff ID</strong></TableCell>
                        <TableCell><strong>Phone</strong></TableCell>
                        <TableCell><strong>Reset By</strong></TableCell>
                        <TableCell><strong>Date</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentResets.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ width: 30, height: 30, bgcolor: '#1976D2' }}>
                                <Person sx={{ fontSize: 18 }} />
                              </Avatar>
                              {log.staff_name}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={log.staff_id} variant="outlined" />
                          </TableCell>
                          <TableCell>{log.staff_phone}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={log.reset_by_name}
                              icon={<AdminPanelSettings sx={{ fontSize: 14 }} />}
                              color="primary"
                            />
                          </TableCell>
                          <TableCell>
                            {new Date(log.created_at).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </StyledPaper>
          </Grid>
        </Grid>

        {/* Confirmation Dialog */}
        <Dialog
          open={openConfirmDialog}
          onClose={() => setOpenConfirmDialog(false)}
          maxWidth="sm"
          fullWidth
          TransitionComponent={Slide}
          TransitionProps={{ direction: 'up' }}
        >
          <DialogTitle sx={{ bgcolor: '#09083b', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <WarningAmber />
              <Typography variant="h6">Confirm Password Reset</Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>⚠️ You are about to reset the password for:</strong>
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body1">
                  <strong>Name:</strong> {selectedStaff?.first_name} {selectedStaff?.other_names}
                </Typography>
                <Typography variant="body1">
                  <strong>Staff ID:</strong> {selectedStaff?.staff_id}
                </Typography>
                <Typography variant="body1">
                  <strong>Phone:</strong> {selectedStaff?.phone}
                </Typography>
                <Typography variant="body1">
                  <strong>Email:</strong> {selectedStaff?.email}
                </Typography>
                <Typography variant="body1">
                  <strong>Department:</strong> {selectedStaff?.department}
                </Typography>
              </Box>
            </Alert>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>New Password:</strong> {newPassword}
              </Typography>
            </Alert>

            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>⚠️ This action cannot be undone!</strong> The staff member will need to use this new password to log in.
              </Typography>
            </Alert>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setOpenConfirmDialog(false)} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={confirmResetPassword}
              variant="contained"
              color="error"
              disabled={resetting}
              startIcon={resetting ? <CircularProgress size={20} /> : <CheckCircle />}
            >
              {resetting ? 'Resetting...' : 'Confirm Reset'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          TransitionComponent={Slide}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default ResetPassword;